<template>
  <div>
    <v-dialog v-model="dialog" persistent max-width="1200px">
      <v-card class="d-flex" style="flex-direction: column">
        <v-card-title class="font-weight-bold pb-0">
          Lista de Balanço Financeiro da Unidade Consumidora {{ consumerUnit }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            dark
            flat
            color="error"
            @click="$emit('closeDialog', false)"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-data-table
          hide-actions
          class="pt-3"
          :headers="accountBalancesHeaders"
          :items="accountBalancesTable"
        >
          <template v-slot:items="props">
            <td class="text-xs-center">
              {{ props.item.category | formatCategoryType }}
            </td>
            <td class="text-xs-center">
              {{ props.item.value | formatMoney }}
            </td>
          </template>

          <template v-slot:no-data>
            <p class="text-md-center">Sem balanço financeiro disponível</p>
          </template>
        </v-data-table>

        <v-data-table
          class="pt-3"
          :headers="headers"
          :items="historyTable"
          :pagination.sync="pag"
        >
          <template v-slot:items="props">
            <td class="text-xs-center">
              {{ props.item.created_at | formatDateTime }}
            </td>
            <td class="text-xs-center">
              {{ props.item.category | formatCategoryType }}
            </td>
            <td class="text-xs-center">
              {{ props.item.type }}
            </td>
            <td class="text-xs-center">
              <v-chip
                :color="props.item.status | formatBalanceTypeColor"
                text-color="white"
              >
                {{ props.item.status | formatBalanceType }}
              </v-chip>
            </td>
            <td class="text-xs-center">
              {{ props.item.value | formatMoney }}
            </td>
            <td class="text-xs-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="setExtraInfo(props.item)"
                  >
                    <v-icon dark right color="grey lighten-1">
                      mdi-format-list-bulleted
                    </v-icon>
                  </v-btn>
                </template>
                <span>Visualizar informações extras</span>
              </v-tooltip>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="showExtraInfo"
      v-model="showExtraInfo"
      persistent
      lazy
      max-width="1000px"
    >
      <v-card class="d-flex dialog-container" style="flex-direction: column">
        <v-card-title class="title font-weight-bold pb-0 dialog-header">
          Informações extras sobre aplicação do Balanço Financeiro
          <v-spacer></v-spacer>
          <v-btn icon dark flat color="error" @click="showExtraInfo = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="dialog-content">
          <v-timeline align-top dense>
            <v-timeline-item color="#7D3DFF">
              <v-layout pt-2>
                <v-flex xs4>
                  <v-layout column>
                    <v-flex>
                      <span :class="`subheading font-weight-bold black--text`"
                        >{{ actualHistory.type }} de R$
                        {{
                          actualHistory.value.toFixed(2).replace(".", ",")
                        }}</span
                      >
                    </v-flex>
                    <v-flex>
                      <span
                        :class="`caption font-weight-bold black--text`"
                        v-text="`Agente: `"
                      ></span>
                      <span
                        :class="`caption font-weight-light black--text`"
                        v-text="actualHistory.agent_email"
                      ></span>
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex>
                  <span :class="`font-weight-bold mb-3 black--text`">
                    {{ actualHistory.description }}
                  </span>
                  <div v-if="actualHistory.reason">
                    <span
                      :class="`caption font-weight-bold black--text`"
                      v-text="`Motivo: `"
                    ></span>
                    <span
                      :class="`caption font-weight-light black--text`"
                      v-text="actualHistory.reason"
                    ></span>
                  </div>
                </v-flex>
              </v-layout>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import dayjs from "dayjs";

export default {
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    consumerUnit: {
      type: String,
      required: true,
    },
    accountBalances: {
      type: Array,
      required: true,
    },
    history: {
      type: Array,
      required: true,
    },
  },

  data: () => ({
    actualHistory: {},
    showExtraInfo: false,
    accountBalancesHeaders: [
      {
        text: "Categoria",
        align: "center",
        value: "category",
      },
      {
        text: "Valor",
        align: "center",
        value: "value",
      },
    ],
    headers: [
      {
        text: "Data",
        align: "center",
        value: "created_at",
      },
      {
        text: "Categoria",
        align: "center",
        value: "category",
      },
      {
        text: "Tipo",
        align: "center",
        value: "type",
      },
      {
        text: "Status",
        align: "center",
        value: "status",
      },
      {
        text: "Valor",
        align: "center",
        value: "value",
      },
      {
        text: "Informações Extras",
        align: "center",
        value: "",
      },
    ],
    pag: {
      rowsPerPage: 20,
      sortBy: "created_at",
      descending: true,
    },
  }),

  computed: {
    accountBalancesTable() {
      const mappedBalances = this.accountBalances.map((ab) => {
        return {
          category: ab.category,
          value: ab.value < 0 ? ab.value * -1 : ab.value,
        };
      });

      return mappedBalances.reduce((acc, curr) => {
        const existingCategory = acc.find(
          (item) => item.category === curr.category,
        );
        if (existingCategory) {
          existingCategory.value += curr.value;
        } else {
          acc.push({ category: curr.category, value: curr.value });
        }
        return acc;
      }, []);
    },

    historyTable() {
      return this.history.map((h) => {
        return {
          ...h,
          status: h.type,
          value: h.value < 0 ? h.value * -1 : h.value,
          type: h.value < 0 ? "Cobrança Futura" : "Bônus",
        };
      });
    },
  },

  methods: {
    setExtraInfo(history) {
      this.actualHistory = history;
      this.showExtraInfo = true;
    },
  },

  filters: {
    formatDateTime: (date) => {
      if (!date || date === "") return "";
      if (typeof date === "string") return dayjs(date).format("DD/MM/YYYY");
      else return dayjs(date.toDate()).format("DD/MM/YYYY HH:mm");
    },

    formatCategoryType: (category) => {
      switch (category) {
        case "duplicated_payment":
          return "Pagamento em duplicidade";
        case "marketing":
          return "Marketing";
        case "utility_interest":
          return "Juros de concessionária";
        case "greater_payment":
          return "Pagamento maior";
        case "retention":
          return "Retenção";
        case "minor_payment":
          return "Pagamento à menor";
        case "wrong_transfer":
          return "Repasse Indevido";
        case "not_billed":
          return "Não faturado";
        case "utility_rebilling":
          return "Refaturamento de concessionária";
        case "general":
          return "Sem categoria";
        default:
          return "Sem categoria";
      }
    },

    formatBalanceType: (type) => {
      if (!type || type === "") return "Sem tipo";
      if (type === "credit") return "Entrada";
      else return "Saída";
    },

    formatBalanceTypeColor: (type) => {
      if (!type || type === "") return "Grey darken-2";
      if (type === "credit") return "green";
      else return "red";
    },

    formatMoney: (value) => {
      if (!value) return "-";
      return "R$ " + value.toFixed(2).replace(".", ",");
    },
  },
};
</script>
